import { useEffect, useMemo, useRef, useState } from "react";
import { C, testerPools } from "@bd/content";
import { closeupPerson, look, LOOK_COUNT, type Look } from "@bd/art";
import { dateOf, hashString, hasRoom, money, priceIndex, type Company, type PlaytestResult, type Project } from "@bd/sim";
import { Icon, Why, Meter } from "@bd/ui-kit";
import { me, useGame } from "../store";
import { CloseupCanvas, type Actor, type CloseupHandle } from "../world/CloseupCanvas";
import { dataUrl } from "../world/textures";
import { sfx } from "../lib/sound";
import { t as tr, tp, tx } from "../i18n";

const DURATION = 12; // seconds to watch a full round (Skip jumps to the end)

const MOOD_COLOR: Record<string, string> = { hooked: "var(--green)", winning: "var(--green)", happy: "var(--teal)", bored: "var(--warn)", arguing: "var(--gold)", confused: "var(--warn)", frustrated: "var(--bad)" };

export function testerLook(name: string): Look {
  return look(hashString(name) % LOOK_COUNT);
}

/**
 * Looks for this session's testers: each name keeps its look from round to round, and no two testers share one.
 * Starting from the name's own look, it steps on to the first look whose hair style and colour nobody has yet
 * (then, once those run out, any unused look).
 */
function assignLooks(names: string[], seen: Map<string, number>): void {
  const sig = (i: number) => {
    const lk = look(i);
    return `${lk.style}|${lk.hair.join(",")}`;
  };
  for (const name of names) {
    if (seen.has(name)) continue;
    const used = new Set(seen.values());
    const usedSig = new Set([...seen.values()].map(sig));
    const start = hashString(name) % LOOK_COUNT;
    let pick = -1;
    for (let k = 0; k < LOOK_COUNT && pick < 0; k++) if (!used.has((start + k) % LOOK_COUNT) && !usedSig.has(sig((start + k) % LOOK_COUNT))) pick = (start + k) % LOOK_COUNT;
    for (let k = 0; k < LOOK_COUNT && pick < 0; k++) if (!used.has((start + k) % LOOK_COUNT)) pick = (start + k) % LOOK_COUNT;
    seen.set(name, pick < 0 ? start : pick);
  }
}

function FunCurve({ curve, upTo, dip }: { curve: number[]; upTo: number; dip?: { i: number; label: string } }) {
  const w = 300;
  const h = 90;
  const n = Math.max(2, Math.ceil(curve.length * upTo));
  const pts = curve.slice(0, n).map((v, i) => [(i / (curve.length - 1)) * w, h - 6 - Math.max(0, Math.min(1, (v - 0.2) / 0.7)) * (h - 12)] as const);
  const d = pts.map(([x, y], i) => `${i ? "L" : "M"}${x.toFixed(1)},${y.toFixed(1)}`).join(" ");
  return (
    <svg viewBox={`0 0 ${w} ${h}`} width="100%" height={h} role="img" aria-label={tr("Fun, turn by turn")}>
      <path d={`${d} L${pts[pts.length - 1][0]},${h} L0,${h} Z`} fill="var(--gold)" opacity={0.18} />
      <path d={d} fill="none" stroke="var(--gold)" strokeWidth={3} strokeLinejoin="round" strokeLinecap="round" />
      {dip && dip.i < n && <><circle cx={pts[dip.i][0]} cy={pts[dip.i][1]} r={6} fill="var(--warn)" stroke="var(--ink)" strokeWidth={2} /><text x={Math.min(w - 70, pts[dip.i][0] + 8)} y={pts[dip.i][1] + 18} fontSize={12} fill="var(--warn)" fontWeight={700}>{dip.label}</text></>}
    </svg>
  );
}

export function PlaytestScreen({ projectId }: { projectId: string }) {
  const game = useGame((s) => s.game)!;
  const set = useGame((s) => s.set);
  const cmd = useGame((s) => s.cmd);
  const toast = useGame((s) => s.toast);
  const quality = useGame((s) => s.quality);
  const co = me(game) as Company;
  const p = co.projects.find((x) => x.id === projectId) as Project | undefined;
  const [handle, setHandle] = useState<CloseupHandle | null>(null);
  const [t, setT] = useState(1); // 0..1 through the current round's replay
  const [paused, setPaused] = useState(false);
  const [fast, setFast] = useState(false);
  const [round, setRound] = useState<PlaytestResult | null>(() => p?.playtests[p.playtests.length - 1] ?? null);
  const pools = testerPools.filter((pl) => (pl.needsRoom ? hasRoom(co, pl.needsRoom) : co.unlocked.includes(`p:${pl.id}`)));
  // start on the free pool; paid testers are a choice, never a surprise charge
  const [pool, setPool] = useState(pools.find((pl) => !pl.cost)?.id ?? pools[0]?.id ?? "friends");
  const raf = useRef(0);
  // tester looks for this session: no two testers alike, the same person the same from round to round
  const looks = useRef(new Map<string, number>());
  assignLooks((round?.testers ?? []).map((ts) => ts.name), looks.current);
  const lookOf = (name: string) => look(looks.current.get(name) ?? hashString(name) % LOOK_COUNT);

  // animate the round
  useEffect(() => {
    if (!round || t >= 1) return;
    let last = performance.now();
    const step = (now: number) => {
      const dt = (now - last) / 1000;
      last = now;
      if (!paused) setT((v) => Math.min(1, v + (dt / DURATION) * (fast ? 4 : 1)));
      raf.current = requestAnimationFrame(step);
    };
    raf.current = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf.current);
  }, [round, paused, fast, t >= 1]);

  // the table sounds like a game being played: a die or a card each turn, a thud when someone hits a problem
  const turn = round && t < 1 ? Math.floor(t * 10) : -1;
  useEffect(() => {
    if (turn < 0 || !round) return;
    const hit = round.problems.some((x) => x.turn === turn);
    sfx(hit ? "thud" : turn % 2 ? "card" : "dice");
  }, [turn, round]);

  useEffect(() => {
    const k = (e: KeyboardEvent) => {
      if (e.key === " ") {
        e.preventDefault();
        e.stopPropagation();
        setPaused((v) => !v);
      } else if (e.key.toLowerCase() === "f") setFast((v) => !v);
      // Escape closes an open "why" first, not the whole playtest
      else if (e.key === "Escape" && !document.querySelector(".popover")) set({ screen: null });
    };
    window.addEventListener("keydown", k, true);
    return () => window.removeEventListener("keydown", k, true);
  }, [set]);

  const actors: Actor[] = useMemo(() => {
    const out: Actor[] = [];
    const testers = round?.testers ?? [];
    testers.slice(0, 6).forEach((ts, i) => {
      const evs = (round?.events ?? []).filter((e) => e.tester === i && e.t <= t && e.t > t - 0.06);
      const cheer = evs.some((e) => e.kind === "win" || e.kind === "cheer" || (e.kind === "roll" && e.line));
      const bored = evs.some((e) => e.kind === "lean_back" || e.kind === "phone") || (t >= 1 && (ts.mood === "bored" || ts.mood === "frustrated"));
      const mood = t >= 1 ? ts.mood : "happy";
      out.push({ id: ts.name, look: lookOf(ts.name), seat: i, armsUp: cheer, lean: bored ? 1 : 0, mouth: cheer || mood === "hooked" || mood === "winning" ? "grin" : bored ? "flat" : evs.some((e) => e.kind === "argue" || e.kind === "slam") ? "open" : "smile" });
    });
    out.push({ id: "you", look: look(co.staff[0]?.look ?? 3), seat: -1, armsUp: false, lean: 0, mouth: "smile" });
    return out;
  }, [round, t, co.staff]);

  if (!p) {
    return (
      <div className="closeup-hud">
        <div className="panel" style={{ position: "fixed", left: 24, top: 100, padding: 18 }}>{tr("That project has moved on.")} <button className="btn small" onClick={() => set({ screen: null })}>{tr("Back")}</button></div>
      </div>
    );
  }
  const d = p.design;
  const bubbles = (round?.events ?? []).filter((e) => e.line && e.t <= t && e.t > t - 0.2).slice(-2);
  const knownFix = (pid: string) => p.fixes.includes(pid as any) || p.fixing.some((f) => f.problem === pid);
  const run = async () => {
    const r = await cmd({ type: "playtest", projectId: p.id, pool });
    if (!r.ok) return toast({ title: tr("Can't run a playtest"), text: tr(r.error), tone: "warn" });
    setRound(r.info as PlaytestResult);
    setT(0);
    setPaused(false);
  };
  const fix = async (problem: string) => {
    const r = await cmd({ type: "fix", projectId: p.id, problem: problem as any });
    if (!r.ok) toast({ title: tr("Can't fix that"), text: tr(r.error), tone: "warn" });
    else toast({ title: tr("Fixing: {fix}", { fix: tr(C.problem[problem].fix) }), text: tr("About {time} of work.", { time: weeksText((r.info as any).weeks) }), tone: "info" });
  };
  const moveOn = async () => {
    if (p.phase === "playtest") await cmd({ type: "advance", projectId: p.id });
    set({ screen: null });
    useGame.getState().resume();
  };
  const shown = round && t >= 1 ? round.problems : (round?.problems ?? []).filter((x) => x.turn / 10 <= t);
  const dip = round ? round.funCurve.reduce((best, v, i) => (v < round.funCurve[best] ? i : best), 0) : 0;
  const dipLabel = shown[0] ? tr(C.problem[shown[0].problem].name).toLowerCase() : undefined;
  const replay = round ? round.replayIntent : null;
  const playable = p.phase !== "prototype" || p.work.prototype.done >= p.work.prototype.need * 0.5;
  const date = dateOf(game.week);
  const poolDef = C.pool[pool];

  return (
    <>
      <CloseupCanvas kind="playtest" year={date.year} level={co.level} table={{ mechanic: d.mechanic, theme: d.theme, format: d.format, name: d.name }} actors={actors} quality={quality} onReady={setHandle} />
      <div className="closeup-hud" data-testid="playtest">
        <div className="panel pt-title">
          <button className="icon-btn pt-back" onClick={() => set({ screen: null })} aria-label={tr("Back to the studio")} title={tr("Back to the studio")} data-testid="pt-back"><Icon name="back" size={20} /></button>
          <h2 className="display" style={{ margin: 0 }}>{d.name}</h2>
          <div className="muted pt-meta">{tr(C.audience[d.audience].name)} · {d.players[0]}–{d.players[1]} · {tr("{n} min", { n: d.playTime })}</div>
        </div>

        {handle && bubbles.map((b, i) => {
          const seat = handle.scene.seats[b.tester];
          if (!seat) return null;
          const s = handle.toScreen(seat.hx, seat.hy - 4);
          return (
            <div key={`${b.t}-${i}`} className="bubble" style={{ left: `clamp(min(160px, 37vw), ${s.x}px, calc(100vw - min(160px, 37vw)))`, top: s.y }}>
              {tx(b.line ?? "")}
            </div>
          );
        })}

        <div className={`panel readout ${round ? "" : "idle"}`} data-testid="readout">
          {round && (
            <>
              <FunCurve curve={round.funCurve} upTo={t} dip={dipLabel ? { i: dip, label: dipLabel } : undefined} />
              <div className="spread" style={{ alignItems: "center" }} title={tr("Would play again")}>
                <span className="row" style={{ gap: 6 }} aria-label={tr("Would play again")}><Icon name="heart" size={18} color="var(--coral)" />
                  <Why title={tr("Would play again")} lines={[{ label: tr("Testers who'd play again"), value: replay !== null ? `${Math.round(replay * 100)}%` : "?" }, { label: tr("Keeps selling for years at"), value: "70%" }, { label: tr(poolDef.name), value: poolDef.kindness > 0.05 ? tr("too kind") : tr("honest") }]} note={tr(poolDef.note)} />
                </span>
                <span className="display" style={{ fontSize: "2rem" }} data-testid="replay">{replay !== null && t >= 0.85 ? `${Math.round(replay * 100)}%` : "…"}</span>
              </div>
              <div className="replay-bar" title={tr("70% = keeps selling for years")}><i style={{ width: `${(replay !== null && t >= 0.85 ? replay : 0) * 100}%` }} /><b style={{ left: "70%" }} /></div>
              {shown.length > 0 && (
                <div className="stack" style={{ gap: 6, marginTop: 10 }}>
                  {shown.map((s) => (
                    <div key={s.problem} className="problem" title={tr(C.problem[s.problem].see)}>
                      <span className="dot" style={{ background: s.confidence === "found" ? "var(--warn)" : "var(--muted)" }} />
                      <strong style={{ flex: 1 }}>{tr(C.problem[s.problem].name)}</strong>
                      {s.confidence === "found" && !knownFix(s.problem) && t >= 1 && (
                        <button className="btn small fix-btn" onClick={() => fix(s.problem)} data-testid="fix" title={tr(C.problem[s.problem].fix)}>{tr("Fix")} · {C.problem[s.problem].fixWeeks < 1 ? tr("3 days") : tp(C.problem[s.problem].fixWeeks, "{n} wk", "{n} wks")}</button>
                      )}
                      {knownFix(s.problem) && <span className="muted" style={{ fontSize: "0.8rem" }}>{tr("fixing")}</span>}
                    </div>
                  ))}
                </div>
              )}
              {round.focusNote && t >= 0.7 && (
                <div className="inset row" style={{ padding: "6px 10px", marginTop: 8, fontSize: "0.85rem", gap: 8 }} title={`"${round.focusNote.quote}"`}>
                  <span style={{ flex: 1 }}>→ {tr(round.focusNote.more ? "more {slider}" : "less {slider}", { slider: tr(({ depth: "Depth", access: "Accessibility", immersion: "Theme immersion", interaction: "Interaction", replay: "Replayability", art: "Art", components: "Components", speed: "Speed" } as Record<string, string>)[round.focusNote.slider]) })}</span>
                  <button className="btn small ghost" onClick={() => set({ screen: { kind: "design", projectId: p.id, back: "playtest" } })}>{tr("Adjust")}</button>
                </div>
              )}
            </>
          )}
          <div className="readout-actions stack" style={{ gap: 8 }}>
            {playable ? (
              <div className="row" style={{ flexWrap: "nowrap", gap: 8 }}>
                <select value={pool} onChange={(e) => setPool(e.target.value)} aria-label={tr("Tester pool")} title={tr(poolDef.note)} style={{ flex: 1, minWidth: 0 }}>
                  {pools.map((pl) => <option key={pl.id} value={pl.id}>{tr(pl.name)}{pl.cost ? ` · ${money(pl.cost * priceIndex(game.week))}` : ""}</option>)}
                </select>
                <button className={p.playtests.length === 0 ? "btn primary" : "btn"} onClick={run} disabled={!!round && t < 1} data-testid="run-playtest">{round ? <><Icon name="replay" size={16} /> {tr("Again")}</> : <><Icon name="play" size={16} color="#fff" /> {tr("Play a round")}</>}</button>
              </div>
            ) : (
              <div className="inset" style={{ padding: "8px 12px" }} data-testid="not-playable">{tr("Prototype {pct}%", { pct: Math.round((p.work.prototype.done / Math.max(1, p.work.prototype.need)) * 100) })}<Meter value={p.work.prototype.done / Math.max(1, p.work.prototype.need)} color="var(--coral)" style={{ marginTop: 6 }} /></div>
            )}
            {p.phase === "playtest" && <button className={p.playtests.length === 0 ? "btn ghost" : "btn primary"} onClick={moveOn} data-testid="move-on">{tr("Art and rules")} →</button>}
          </div>
        </div>

        {round && (
          <div className="panel testers">
            {round.testers.slice(0, 4).map((ts) => (
              <div key={ts.name} className="tester" title={tr(C.personality[ts.personality].name)}>
                <img src={dataUrl(closeupPerson(lookOf(ts.name), { view: "front", mouth: t >= 1 && (ts.mood === "bored" || ts.mood === "frustrated") ? "flat" : "smile" }).crop(0, 0, 28, 28), 3)} width={72} height={72} alt="" style={{ imageRendering: "pixelated" }} />
                <div style={{ minWidth: 0 }}>
                  <div className="display" style={{ fontSize: "1.15rem" }}>{ts.name}</div>
                  {t >= 1 && <div style={{ color: MOOD_COLOR[ts.mood], fontWeight: 700, fontSize: "0.9rem" }}>● {tr(ts.mood[0].toUpperCase() + ts.mood.slice(1), undefined, "mood")}</div>}
                </div>
              </div>
            ))}
          </div>
        )}
        <div className="keys">
          <span><kbd>{tr("Space", undefined, "key")}</kbd> {paused ? tr("play") : tr("pause")}</span>
          <span><kbd>F</kbd> {fast ? tr("normal speed") : tr("fast-forward")}</span>
          <span><kbd>Esc</kbd> {tr("studio")}</span>
          {round && t < 1 && <button className="btn small" onClick={() => setT(1)}>{tr("Skip to the end")}</button>}
        </div>
        <button className="btn back-btn" onClick={() => set({ screen: null })}><Icon name="back" size={18} /> {tr("Studio")}</button>
      </div>
    </>
  );
}

/** "half a week", "1 week", "3 weeks" */
function weeksText(w: number): string {
  if (w <= 0.6) return tr("half a week");
  return tp(Math.round(w), "{n} week", "{n} weeks");
}
